import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../../environments/environment';
import { catchError, of } from 'rxjs';

export interface ClientError {
  message: string;
  stackTrace?: string;
  url: string;
  userAgent: string;
  userId?: string;
  level: string;
  timestamp: string;
  component?: string;
  route?: string;
  action?: string;
  additionalData?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ErrorLoggingService {
  private readonly apiUrl = `${environment.apiUrl}/logs/client-error`;
  private errorQueue: ClientError[] = [];
  private isProcessing = false;

  constructor(private http: HttpClient) {}

  /**
   * שולח שגיאה לשרת
   */
  logError(error: Error, context?: {
    component?: string;
    route?: string;
    action?: string;
    userId?: string;
    additionalData?: string;
  }): void {
    const clientError: ClientError = {
      message: error.message || 'Unknown error',
      stackTrace: error.stack,
      url: window.location.href,
      userAgent: navigator.userAgent,
      userId: context?.userId?.toString() || this.getUserIdFromStorage()?.toString(),
      level: 'Error',
      timestamp: new Date().toISOString(),
      component: context?.component,
      route: context?.route || window.location.pathname,
      action: context?.action,
      additionalData: context?.additionalData
    };

    // הוסף לתור
    this.errorQueue.push(clientError);

    // נסה לשלוח
    this.processQueue();
  }

  /**
   * מעבד את התור של שגיאות
   */
  private processQueue(): void {
    if (this.isProcessing || this.errorQueue.length === 0) {
      return;
    }

    this.isProcessing = true;
    const error = this.errorQueue[0];

    this.http.post(this.apiUrl, error)
      .pipe(
        catchError(err => {
          console.error('Failed to log error to server:', err);
          // אם נכשל, נשאיר בתור וננסה שוב מאוחר יותר
          setTimeout(() => {
            this.isProcessing = false;
            this.processQueue();
          }, 5000); // נסה שוב אחרי 5 שניות
          return of(null);
        })
      )
      .subscribe({
        next: () => {
          // הסר מהתור אם הצליח
          this.errorQueue.shift();
          this.isProcessing = false;
          // עבד את השגיאה הבאה
          this.processQueue();
        }
      });
  }

  /**
   * מקבל את מזהה המשתמש מ-localStorage/sessionStorage
   */
  private getUserIdFromStorage(): string | undefined {
    try {
      const userStr = localStorage.getItem('currentUser') || sessionStorage.getItem('currentUser');
      if (userStr) {
        const user = JSON.parse(userStr);
        return user?.id || user?.userId;
      }
    } catch (e) {
      // אם נכשל, פשוט תחזיר undefined
    }
    return undefined;
  }
}
